/**
 * CancelInvoiceModal — cancelar a cobrança de uma fatura.
 *
 * É o ÚNICO caminho de cancelamento, inclusive do boleto: com boleto emitido o backend dispara
 * CANCEL_BILL ao biller e a fatura só é cancelada quando o BOLETO_CANCELED volta — por isso o
 * resultado pode ser assíncrono (`CANCEL_BILL_REQUESTED`) e a lista ainda mostrar a fatura aberta
 * por alguns instantes. Cancelar o boleto por fora deixaria a fatura em cobrança sem boleto.
 */
import { useState } from 'react';
import { toast } from 'react-toastify';
import { FaTimes, FaBan } from 'react-icons/fa';
import { cancelInvoice } from '../services/billingApi';
import { formatBRL } from '../../../../utils/currencyUtils';

interface Props {
    invoice: any;
    onClose: () => void;
    onSuccess: () => void;
}

const CancelInvoiceModal: React.FC<Props> = ({ invoice, onClose, onSuccess }) => {
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const hasBoleto = !!invoice.boleto;

    const handleCancel = async () => {
        if (!reason.trim()) return toast.warn('Informe o motivo do cancelamento');
        setSubmitting(true);
        try {
            const res = await cancelInvoice(invoice.invoiceId ?? invoice.id, { reason: reason.trim() });
            if (res.result === 'CANCEL_BILL_REQUESTED') {
                toast.info('Cancelamento solicitado ao banco — a fatura será cancelada quando o biller confirmar.');
            } else if (res.result === 'ALREADY_CANCELLED') {
                toast.info('Fatura já estava cancelada.');
            } else {
                toast.success('Fatura cancelada.');
            }
            onSuccess();
            onClose();
        } catch (e: any) {
            toast.error(e.message || 'Erro ao cancelar');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-md w-full">
                <div className="flex justify-between items-center mb-3">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <FaBan className="text-red-500" /> Cancelar fatura
                    </h2>
                    <button onClick={onClose} aria-label="Fechar"
                        className="text-gray-400 hover:text-gray-600"><FaTimes size={20} /></button>
                </div>

                <p className="text-xs text-gray-500 mb-3">
                    Fatura {String(invoice.invoiceId).slice(0, 8)} · {formatBRL(invoice.amount)}
                    {' · '}a fatura deixa de ser cobrada.
                </p>

                {hasBoleto && (
                    <div className="px-3 py-2 mb-3 text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded text-amber-800 dark:text-amber-300">
                        Esta fatura tem boleto: o cancelamento é enviado ao banco e a fatura só fecha
                        quando o banco confirmar.
                    </div>
                )}

                <label className="block text-sm font-medium mb-1" htmlFor="motivo-cancelamento">
                    Motivo
                </label>
                <textarea
                    id="motivo-cancelamento"
                    className="w-full text-sm border rounded p-2 mb-3 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    rows={2}
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder="Motivo do cancelamento"
                />

                <div className="flex gap-2 justify-end">
                    <button onClick={onClose} disabled={submitting}
                        className="px-4 py-2 text-sm rounded bg-gray-200 dark:bg-slate-600 hover:bg-gray-300">
                        Voltar
                    </button>
                    <button onClick={handleCancel} disabled={submitting}
                        className="px-4 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-60">
                        {submitting ? 'Cancelando...' : 'Cancelar fatura'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CancelInvoiceModal;
